import {useLoader} from "../../../../../hooks/useLoader";
import {useForm} from "react-hook-form";
import {
  OperacionTransferenciasTercerosChequeosComplianceInsert,
  OperacionTransferenciasTercerosChequeosComplianceVista,
  schemaOperacionTransferenciasTercerosChequeosComplianceInsert
} from "../types/dto";
import {zodResolver} from "@hookform/resolvers/zod/dist/zod";
import {OperacionTransferenciasTercerosChequeosComplianceFields} from "../types/fields";
import React, {useCallback, useEffect, useState} from "react";
import {HttpTransferenciaTerceros} from "../api/httpTransferenciaTerceros";
import toast from "react-hot-toast";
import {Button, Grid} from "@mui/material";
import {ControlledCheckBox, ControlledTextField} from "../../../../../components/forms";
import Divider from "@mui/material/Divider";
import {EntityWithIdFields} from "src/lib/itaptec/http_client/src";
import {z} from "../../../../../lib";

interface TransferenciaTercerosChequeosComplianceFormProps {
  idOperacion: number,
  permiteEditar?: boolean,
}

export const TransferenciaTercerosChequeosComplianceForm = ({
                                                              idOperacion,
                                                              permiteEditar
                                                            }: TransferenciaTercerosChequeosComplianceFormProps) => {
  const {showLoader, hideLoader} = useLoader()
  const [chequeosCompliance, setChequeosCompliance] = useState<OperacionTransferenciasTercerosChequeosComplianceVista>()
  const [enableObservationsPago, setEnableObservationsPago] = useState<boolean>(false)
  const [enableObservationsBusqueda, setEnableObservationsBusqueda] = useState<boolean>(false)
  const [enableObservationsCallback, setEnableObservationsCallback] = useState<boolean>(false)

  const loadDefaultValues = () => {
    return new Promise<OperacionTransferenciasTercerosChequeosComplianceVista>((resolve, reject) => {
      return HttpTransferenciaTerceros.getChequeosCompliance(idOperacion).then(chequeosCompliance => {
        const valoresDefault = {
          [OperacionTransferenciasTercerosChequeosComplianceFields.RecibidaInstruccion]: false,
          [OperacionTransferenciasTercerosChequeosComplianceFields.DocumentacionEnOrden]: false,
          [OperacionTransferenciasTercerosChequeosComplianceFields.BeneficiarioRegulado]: false,
          [OperacionTransferenciasTercerosChequeosComplianceFields.InstruccionFirmada]: false,
          [OperacionTransferenciasTercerosChequeosComplianceFields.PagoDirecto]: false,
          [OperacionTransferenciasTercerosChequeosComplianceFields.HitBusqueda]: false,
          [OperacionTransferenciasTercerosChequeosComplianceFields.Callback]: false,
          [OperacionTransferenciasTercerosChequeosComplianceFields.MotivoCliente]: '',
          [OperacionTransferenciasTercerosChequeosComplianceFields.DetallePagoDirecto]: '',
          [OperacionTransferenciasTercerosChequeosComplianceFields.DetalleHitBusqueda]: '',
          [OperacionTransferenciasTercerosChequeosComplianceFields.DetalleCallback]: '',
          [OperacionTransferenciasTercerosChequeosComplianceFields.Opinion]: '',
          [OperacionTransferenciasTercerosChequeosComplianceFields.Situacion]: '',
        };

        // para juntar los valores default y los chequeos compliance que vienen de la api.
        // Como puede ser undefined, sino usamos ademas los valores default nos quedan en undefined los checkbox y lanzan errores de validacion de form.
        const valoresDefaultMerged = {...valoresDefault, ...chequeosCompliance};

        setChequeosCompliance(valoresDefaultMerged);
        resolve(valoresDefaultMerged);

      })
    })
  }

  const {
    control,
    handleSubmit, 
    watch,
    formState: {errors}
  } = useForm<OperacionTransferenciasTercerosChequeosComplianceInsert>({
    resolver: zodResolver(schemaOperacionTransferenciasTercerosChequeosComplianceInsert),
    defaultValues: loadDefaultValues,

  })

  const alConfirmarChequeos = useCallback((data: OperacionTransferenciasTercerosChequeosComplianceInsert) => {
    showLoader();

    if (!permiteEditar) {
      return;
    }

    const promise = chequeosCompliance?.id
      ? HttpTransferenciaTerceros.putChequeosCompliance(idOperacion, {
        [EntityWithIdFields.Id]: chequeosCompliance.id,
        ...data
      })
      : HttpTransferenciaTerceros.postChequeosCompliance(idOperacion, {
        ...data,
      });

    toast.promise(promise, {
      loading: 'Guardando',
      success: 'Guardado correctamente.',
      error: 'Error al guardar.'
    })
      .then((r) => console.log(r))
      .catch(hideLoader)
      .finally(hideLoader)
  }, [idOperacion, permiteEditar, chequeosCompliance]);
  
    const watchCheckPago = watch(OperacionTransferenciasTercerosChequeosComplianceFields.PagoDirecto)

    useEffect(() => {
        setEnableObservationsPago(!!watchCheckPago)
    }, [watchCheckPago]);

    const watchCheckBusqueda = watch(OperacionTransferenciasTercerosChequeosComplianceFields.HitBusqueda)

    useEffect(() => {
        setEnableObservationsBusqueda(!!watchCheckBusqueda)
    }, [watchCheckBusqueda]);
    
    const watchCheckCallback = watch(OperacionTransferenciasTercerosChequeosComplianceFields.Callback)

    useEffect(() => {
        setEnableObservationsCallback(!!watchCheckCallback)
    }, [watchCheckCallback]);

    const x = 51

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ControlledTextField
          control={control}
          label={'Motivo Cliente'}
          name={OperacionTransferenciasTercerosChequeosComplianceFields.MotivoCliente}
          multiline
          fullWidth
          minRows={2}
          disabled={!permiteEditar}
        />
      </Grid>
      <Grid item xs={12}>
        <Divider>Checklist</Divider>

      </Grid>
      <Grid item xs={12}>
        <ControlledCheckBox
          name={OperacionTransferenciasTercerosChequeosComplianceFields.RecibidaInstruccion}
          control={control}
          label={'Recibida instrucción por medio digital directamente del cliente'}
          disabled={!permiteEditar}
        />
      </Grid>
      <Grid item xs={12}>
        <ControlledCheckBox
          name={OperacionTransferenciasTercerosChequeosComplianceFields.DocumentacionEnOrden}
          control={control}
          label={'Documentación respaldatoria en orden'}
          disabled={!permiteEditar}
        />
      </Grid>
      <Grid item xs={12}>
        <ControlledCheckBox
          name={OperacionTransferenciasTercerosChequeosComplianceFields.BeneficiarioRegulado}
          control={control}
          label={'Banco/Bróker/MSB beneficiario regulado'}
          disabled={!permiteEditar}
        />
      </Grid>

      <Grid item xs={12}>
        <ControlledCheckBox
          name={OperacionTransferenciasTercerosChequeosComplianceFields.InstruccionFirmada}
          control={control}
          label={'Instrucción firmada por la casilla de correo registrada'}
          disabled={!permiteEditar}
        />
      </Grid>

      <Grid item xs={12}>
        <ControlledCheckBox
          name={OperacionTransferenciasTercerosChequeosComplianceFields.PagoDirecto}
          control={control}
          label={'Pago directo a la cuenta del beneficiario en el banco destino'}
          disabled={!permiteEditar}
        />
      </Grid>
        {
            enableObservationsPago &&
              <Grid item xs={11.75}>
                <ControlledTextField
                  control={control}
                  label={'Observaciones'}
                  name={OperacionTransferenciasTercerosChequeosComplianceFields.DetallePagoDirecto}
                  fullWidth
                  sx={{mx: 7}}
                  disabled={!permiteEditar}
                  multiline
                />
              </Grid>
        }
      <Grid item xs={12}>
        <ControlledCheckBox
          name={OperacionTransferenciasTercerosChequeosComplianceFields.HitBusqueda}
          control={control}
          label={'Hit de búsqueda del beneficiario'}
          disabled={!permiteEditar}
        />
      </Grid>
        {
            enableObservationsBusqueda &&
              <Grid item xs={11.75}>
                <ControlledTextField
                  control={control}
                  label={'Observaciones'}
                  name={OperacionTransferenciasTercerosChequeosComplianceFields.DetalleHitBusqueda}
                  fullWidth
                  sx={{mx: 7}}
                  disabled={!permiteEditar}
                  multiline
                />
              </Grid>
        }
      <Grid item xs={12}>
        <ControlledCheckBox
          name={OperacionTransferenciasTercerosChequeosComplianceFields.Callback}
          control={control}
          label={'Callback'}
          disabled={!permiteEditar}
        />
      </Grid>
        {
            enableObservationsCallback &&
              <Grid item xs={11.75}>
                <ControlledTextField
                  control={control}
                  label={'Observaciones'}
                  name={OperacionTransferenciasTercerosChequeosComplianceFields.DetalleCallback}
                  fullWidth
                  sx={{mx: 7}}
                  disabled={!permiteEditar}
                />
              </Grid>
        }
        <Grid container item xs={12} spacing={4} mt={2}>
          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              label={'Opinión del Compliance Officer'}
              name={OperacionTransferenciasTercerosChequeosComplianceFields.Opinion}
              fullWidth
              multiline
              disabled={!permiteEditar}
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              label={'Situación'}
              name={OperacionTransferenciasTercerosChequeosComplianceFields.Situacion}
              fullWidth
              multiline
              disabled={!permiteEditar}
            />
          </Grid>
        </Grid>
      {
        permiteEditar &&
          <Grid item container xs={12} justifyContent={'flex-end'} direction={'row'} mt={3}>
              <Grid item container xs={4} justifyContent={'flex-end'}>
                  <Button variant='contained' color='primary' onClick={handleSubmit(alConfirmarChequeos)}>
                      Confirmar Checklist
                  </Button>
              </Grid>
          </Grid>
      }
    </Grid>
  )
}
